/**
 * Discount code entry.
 *
 * What "Apply" does, and what it deliberately does not do:
 *
 * `/cart/update.js` with a `discount` attribute **stores** the code against the
 * cart. Shopify calculates discounts at checkout, so the storefront subtotal
 * does not move and this element must never suggest that it has. On success it
 * shows the code as a pill and says the code will be applied at checkout; the
 * subtotal beside it is left exactly as the server rendered it.
 *
 * "Valid" here means Shopify echoed the code back on the cart. A code it does
 * not echo back is reported as invalid and cleared, so a shopper is never left
 * believing a rejected code is waiting for them at checkout.
 *
 * Every user-facing string comes from data attributes on the element, written
 * by component-cart-discount.liquid through the translation filter.
 *
 * @extends HTMLElement
 */
export class CartDiscountForm extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRemoveClick = this.handleRemoveClick.bind(this);
  }

  connectedCallback() {
    /* Class selectors, not ids: this element is on the cart page twice (the
       drawer renders on every page), so the ids are per-instance. querySelector
       is already scoped to this element, which is what makes classes safe. */
    this.form = this.querySelector('.cart-discount__form');
    this.input = this.querySelector('.cart-discount__input');
    this.errorEl = this.querySelector('.cart-discount__error');
    this.statusEl = this.querySelector('.cart-discount__status');
    this.codesList = this.querySelector('.cart-discount__codes');
    this.submitBtn = this.form?.querySelector('button[type="submit"]');
    this.originalButtonText = this.submitBtn?.textContent.trim();

    if (!this.form) return;

    this.form.addEventListener('submit', this.handleSubmit);
    this.codesList?.addEventListener('click', this.handleRemoveClick);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.codesList?.removeEventListener('click', this.handleRemoveClick);
  }

  showError(message) {
    this.hideStatus();
    const errorText = this.errorEl?.querySelector('.cart-discount__error-text');
    if (!errorText || !this.errorEl) return;
    errorText.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    const errorText = this.errorEl.querySelector('.cart-discount__error-text');
    if (errorText) errorText.textContent = '';
  }

  showStatus(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.hidden = false;
  }

  hideStatus() {
    if (!this.statusEl) return;
    this.statusEl.hidden = true;
    this.statusEl.textContent = '';
  }

  setLoading(loading) {
    if (this.submitBtn) {
      this.submitBtn.disabled = loading;
      this.submitBtn.textContent = loading ? this.dataset.statusApplying : this.originalButtonText;
    }
    if (this.input) this.input.disabled = loading;
  }

  async applyDiscount(codes) {
    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codes })
    });

    if (!res.ok) throw new Error(`Discount update failed: ${res.status}`);
    return res.json();
  }

  getExistingCodes() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map(pill => pill.dataset.discountCode)
      .filter(Boolean);
  }

  getCartDiscountCodes(cart) {
    const codes = (cart.cart_level_discount_applications || [])
      .filter(app => app.type === 'discount_code')
      .map(app => app.title);

    (cart.items || []).forEach(item => {
      if (item.discounts) {
        item.discounts.forEach(d => d.title && codes.push(d.title));
      }
      if (item.line_level_discount_allocations) {
        item.line_level_discount_allocations.forEach(a => {
          if (a.discount_application?.title) codes.push(a.discount_application.title);
        });
      }
    });

    return [...new Set(codes)];
  }

  createPill(code) {
    const li = document.createElement('li');
    li.className = 'cart-discount__pill';
    li.dataset.discountCode = code;

    const codeP = document.createElement('p');
    codeP.className = 'cart-discount__pill-code';
    codeP.textContent = code;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'cart-discount__pill-remove';
    removeBtn.setAttribute('aria-label', `${this.dataset.removeLabel} ${code}`);

    const existingIcon = this.querySelector('.cart-discount__pill-remove')?.innerHTML;
    if (existingIcon) removeBtn.innerHTML = existingIcon;

    li.appendChild(codeP);
    li.appendChild(removeBtn);
    return li;
  }

  updatePills(cart) {
    if (!this.codesList) return;

    const allDiscountCodes = this.getCartDiscountCodes(cart);
    const currentCodes = this.getExistingCodes();
    const currentPills = Array.from(this.codesList.querySelectorAll('.cart-discount__pill'));

    currentPills.forEach(pill => {
      const code = pill.dataset.discountCode;
      if (!allDiscountCodes.some(c => c.toUpperCase() === code.toUpperCase())) {
        pill.remove();
      }
    });

    allDiscountCodes.forEach(code => {
      if (!currentCodes.some(c => c.toUpperCase() === code.toUpperCase())) {
        this.codesList.appendChild(this.createPill(code));
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.hideError();
    this.hideStatus();

    const code = this.input?.value.trim();
    if (!code) {
      this.showError(this.dataset.errorEmpty);
      return;
    }

    const existing = this.getExistingCodes();
    if (existing.some(c => c.toUpperCase() === code.toUpperCase())) {
      this.input.value = '';
      return;
    }

    this.setLoading(true);
    try {
      const allCodes = [...existing, code].join(',');
      await this.applyDiscount(allCodes);

      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
      const cartCodes = this.getCartDiscountCodes(cart);
      const isApplied = cartCodes.some(c => c.toUpperCase() === code.toUpperCase());

      if (isApplied) {
        this.input.value = '';
        this.updatePills(cart);
        /* Saved for checkout — not applied to the subtotal on this page. */
        this.showStatus(this.dataset.statusSaved);

        window.Cart?.refresh();
      } else {
        this.showError(this.dataset.errorInvalid);
      }
    } catch (err) {
      console.error(err);
      this.showError(this.dataset.errorGeneric);
    } finally {
      this.setLoading(false);
    }
  }

  async handleRemoveClick(event) {
    const removeBtn = event.target.closest('.cart-discount__pill-remove');
    if (!removeBtn) return;

    const pill = removeBtn.closest('.cart-discount__pill');
    const codeToRemove = pill?.dataset.discountCode;
    if (!codeToRemove) return;

    event.preventDefault();
    this.hideError();
    this.hideStatus();

    pill.classList.add('cart-discount__pill--removing');
    removeBtn.disabled = true;

    try {
      const existing = this.getExistingCodes();
      const remaining = existing.filter(c => c.toUpperCase() !== codeToRemove.toUpperCase());

      await this.applyDiscount(remaining.join(','));

      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
      this.updatePills(cart);

      window.Cart?.refresh();
    } catch (err) {
      console.error(err);
      pill.classList.remove('cart-discount__pill--removing');
      removeBtn.disabled = false;
      this.showError(this.dataset.errorRemove);
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
      this.updatePills(cart);
    }
  }
}

if (!customElements.get('cart-discount-form')) {
  customElements.define('cart-discount-form', CartDiscountForm);
}
